'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { ClaiLogoWithShine } from '@lib/chat/shell/clai-fab-visuals';
import { submitFeedback } from '@lib/observability/feedback/submit-feedback.client';
import { isValidEmail } from '@lib/shared/utils/is-valid-email';
import 'client-only';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { useUserDisplay } from '@/hooks/use-user-display';
import { markPromptedThisSession } from './feedback-prompt-dedupe';
export interface ErrorFeedbackCardProps {
  eventId: string | undefined;
  onTryAgain: () => void;
}
type CardState = 'form' | 'submitting' | 'success' | 'submit-error';
export function ErrorFeedbackCard({
  eventId,
  onTryAgain
}: ErrorFeedbackCardProps) {
  const userDisplay = useUserDisplay();
  const [cardState, setCardState] = useState<CardState>('form');
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  // Re-entrancy guard: `<Button disabled={cardState==='submitting'}>` only
  // takes effect after the next render; a rapid double-click can fire two
  // onSubmit events before that. The ref blocks the second one before any
  // network call.
  const submittingRef = useRef(false);

  // Late-prefill: if useUserDisplay was still loading when the card
  // mounted (TanStack Query hasn't hydrated /api/auth/me yet), the
  // `defaultValue=` on the inputs landed as ''. React only honors
  // `defaultValue` on first mount, so a re-render with the resolved
  // user record would not update the field. Write the values through
  // the ref instead — but only when the input is currently empty so
  // we never overwrite what the user has typed. This preserves both
  // the late-prefill guarantee AND the "never lose typing" property.
  useEffect(() => {
    if (userDisplay.isLoading || userDisplay.hasError) return;
    if (nameRef.current && nameRef.current.value === '') {
      nameRef.current.value = userDisplay.userName;
    }
    if (emailRef.current && emailRef.current.value === '') {
      emailRef.current.value = userDisplay.userEmail;
    }
  }, [userDisplay.isLoading, userDisplay.hasError, userDisplay.userName, userDisplay.userEmail]);
  function handleTryAgain() {
    markPromptedThisSession();
    onTryAgain();
  }
  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    const form = event.currentTarget;
    const data = new FormData(form);
    const trying = String(data.get('trying') ?? '').trim();
    const happened = String(data.get('happened') ?? '').trim();
    const name = String(data.get('name') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    const contactConsent = data.get('contactConsent') === 'on';
    if (!isValidEmail(email)) {
      emailRef.current?.setCustomValidity('Enter a valid email address');
      emailRef.current?.reportValidity();
      submittingRef.current = false;
      return;
    }
    emailRef.current?.setCustomValidity('');
    setCardState('submitting');
    try {
      await submitFeedback({
        eventId,
        message: `What I was trying to do:\n${trying}\n\nWhat happened:\n${happened}`,
        name,
        email,
        contactConsent,
        source: 'error_boundary'
      });
      markPromptedThisSession();
      setCardState('success');
    } catch {
      setCardState('submit-error');
    } finally {
      submittingRef.current = false;
    }
  }
  if (cardState === 'success') {
    return <SuccessPanel onTryAgain={handleTryAgain} />;
  }
  return <div className="flex min-h-screen items-center justify-center px-4" data-sentry-component="ErrorFeedbackCard" data-sentry-source-file="error-feedback-card.tsx">
      <div className={cn('bg-foreground/90 w-full max-w-[480px] rounded-2xl p-6 shadow-2xl', 'backdrop-blur-[12.5px]')}>
        <div className="mb-3 flex items-center gap-2.5">
          <ClaiLogoWithShine size="h-7 w-7" data-sentry-element="ClaiLogoWithShine" data-sentry-source-file="error-feedback-card.tsx" />
          <h2 className="text-background text-2xl font-semibold tracking-tight">
            Ask <span className="animate-clai-shimmer">Clai</span>
          </h2>
        </div>
        <p className="text-background/80 mb-5 text-sm">
          Something broke. Tell Clai what happened and we&apos;ll get on it.
        </p>

        {cardState === 'submit-error' && <div role="alert" className="text-background mb-4 rounded-md bg-red-500/20 px-3 py-2 text-sm">
            Couldn&apos;t send right now — try again or just retry the page.
          </div>}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Field label="What were you trying to do?" htmlFor="ef-trying" data-sentry-element="Field" data-sentry-source-file="error-feedback-card.tsx">
            <textarea id="ef-trying" name="trying" rows={2} required autoFocus defaultValue="" className="text-background bg-background/10 w-full rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="What happened?" htmlFor="ef-happened" data-sentry-element="Field" data-sentry-source-file="error-feedback-card.tsx">
            <textarea id="ef-happened" name="happened" rows={4} required defaultValue="" className="text-background bg-background/10 w-full rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Your name" htmlFor="ef-name" data-sentry-element="Field" data-sentry-source-file="error-feedback-card.tsx">
            <input ref={nameRef} id="ef-name" name="name" type="text" required defaultValue={userDisplay.hasError ? '' : userDisplay.userName} className="text-background bg-background/10 w-full rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Email" htmlFor="ef-email" data-sentry-element="Field" data-sentry-source-file="error-feedback-card.tsx">
            <input ref={emailRef} id="ef-email" name="email" type="email" required onInput={() => emailRef.current?.setCustomValidity('')} defaultValue={userDisplay.userEmail} className="text-background bg-background/10 w-full rounded-md px-3 py-2 text-sm" />
          </Field>
          <label className="text-background/80 flex items-center gap-2 text-sm">
            <input type="checkbox" name="contactConsent" defaultChecked={false} />
            It&apos;s OK to contact me about this
          </label>

          <div className="mt-2 flex items-center justify-between">
            <Button type="submit" variant="primary" loading={cardState === 'submitting'} data-sentry-element="Button" data-sentry-source-file="error-feedback-card.tsx">
              Send to Clai
            </Button>
            <Button type="button" variant="link" onClick={handleTryAgain} className="text-background/80 hover:text-background" data-sentry-element="Button" data-sentry-source-file="error-feedback-card.tsx">
              Try Again
            </Button>
          </div>
        </form>
      </div>
    </div>;
}
function Field(t0) {
  const $ = _c(6);
  const {
    label,
    htmlFor,
    children
  } = t0;
  let t1;
  if ($[0] !== htmlFor || $[1] !== label) {
    t1 = <label htmlFor={htmlFor} className="text-background text-sm font-medium">{label}</label>;
    $[0] = htmlFor;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <div className="flex flex-col gap-1" data-sentry-component="Field" data-sentry-source-file="error-feedback-card.tsx">{t1}{children}</div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function SuccessPanel(t0) {
  const $ = _c(5);
  const {
    onTryAgain
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("flex w-full max-w-[480px] flex-col items-center gap-4", "bg-foreground/90 rounded-2xl p-6 text-center shadow-2xl backdrop-blur-[12.5px]");
    t2 = <ClaiLogoWithShine size="h-10 w-10" data-sentry-element="ClaiLogoWithShine" data-sentry-source-file="error-feedback-card.tsx" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p aria-live="polite" className="text-background text-base font-medium">Thanks — <span className="animate-clai-shimmer">Clai</span> got your note.</p>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onTryAgain) {
    t4 = <div className="flex min-h-screen items-center justify-center px-4" data-sentry-component="SuccessPanel" data-sentry-source-file="error-feedback-card.tsx"><div className={t1}>{t2}{t3}<Button type="button" variant="primary" onClick={onTryAgain} data-sentry-element="Button" data-sentry-source-file="error-feedback-card.tsx">Try Again</Button></div></div>;
    $[3] = onTryAgain;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}